import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { API } from "@/utils/constants";

const FIELDS = [
  { key: "name", label: "Contact Name", hint: "required if no email" },
  { key: "email", label: "Email", hint: "used to skip duplicates" },
  { key: "phone", label: "Phone" },
  { key: "company", label: "Company", hint: "auto-created & linked" },
  { key: "notes", label: "Notes" },
  { key: "deal_title", label: "Deal / Lead Title", deal: true },
  { key: "deal_value", label: "Deal Value ($)", deal: true },
  { key: "deal_stage", label: "Deal Stage", deal: true, hint: "lead, contacted, proposal, won, lost" },
];

export const CRMImportDialog = ({ open, onClose, onDone }) => {
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [createDeals, setCreateDeals] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setParsed(null); setMapping({}); setCreateDeals(false); setResult(null); };
  const close = () => { reset(); onClose(); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/crm/import/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      setCreateDeals("deal_title" in (data.suggested_mapping || {}) || "deal_value" in (data.suggested_mapping || {}));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the file");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/crm/import/execute`, {
        rows: parsed.rows, mapping, create_deals: createDeals, default_stage: "lead",
      });
      setResult(data);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const visibleFields = FIELDS.filter((f) => !f.deal || createDeals);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="crm-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-500" />Import Contacts & Leads</DialogTitle></DialogHeader>

        {result ? (
          <div className="py-6 text-center" data-testid="import-result">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-emerald-500" /></div>
            <h3 className="font-semibold text-lg mb-4">Import complete!</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-md mx-auto mb-6">
              {[["Contacts", result.contacts_created], ["Companies", result.companies_created], ["Deals", result.deals_created], ["Skipped", result.skipped]].map(([l, v]) => (
                <div key={l} className="bg-muted/50 rounded-xl p-3"><div className="text-xl font-bold">{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
              ))}
            </div>
            {result.skipped > 0 && <p className="text-xs text-muted-foreground mb-4">Skipped rows had no name/email or matched an existing contact's email.</p>}
            <Button onClick={close} data-testid="import-done-btn">Done</Button>
          </div>
        ) : !parsed ? (
          <div className="py-4">
            <button onClick={() => fileRef.current?.click()} disabled={parsing}
              className="w-full border-2 border-dashed rounded-2xl p-10 text-center hover:border-primary/50 hover:bg-primary/5 transition-colors"
              data-testid="import-dropzone">
              {parsing ? <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-primary" /> : <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />}
              <p className="font-medium text-sm mb-1">{parsing ? "Reading your file..." : "Click to choose your Excel or CSV file"}</p>
              <p className="text-xs text-muted-foreground">.xlsx or .csv — first row must be column headers (e.g. Name, Email, Company). Up to 2,000 rows.</p>
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" onChange={handleFile} data-testid="import-file-input" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between bg-muted/50 rounded-xl px-4 py-2.5">
              <p className="text-sm"><span className="font-semibold">{parsed.total_rows}</span> rows detected{parsed.truncated ? " (capped at 2,000)" : ""}</p>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={reset} data-testid="import-change-file">Change file</Button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">Match your columns</h4>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  Also create deals/leads
                  <Switch checked={createDeals} onCheckedChange={setCreateDeals} data-testid="import-create-deals-toggle" />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {visibleFields.map((f) => (
                  <div key={f.key}>
                    <label className="text-xs font-medium text-muted-foreground">{f.label}{f.hint && <span className="text-[10px] font-normal"> · {f.hint}</span>}</label>
                    <Select value={mapping[f.key] !== undefined ? String(mapping[f.key]) : "skip"}
                      onValueChange={(v) => setMapping((m) => { const n = { ...m }; if (v === "skip") delete n[f.key]; else n[f.key] = parseInt(v); return n; })}>
                      <SelectTrigger className="h-9 mt-1" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">— Skip —</SelectItem>
                        {parsed.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Preview</h4>
              <div className="border rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50"><tr>{parsed.headers.map((h, i) => <th key={i} className="text-left p-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>
                    {parsed.rows.slice(0, 4).map((r, i) => (
                      <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="p-2 text-muted-foreground whitespace-nowrap max-w-[140px] truncate">{c || "—"}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={runImport} disabled={importing || (mapping.name === undefined && mapping.email === undefined)} data-testid="import-run-btn">
                {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                Import {parsed.total_rows} rows
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
